import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import drEasyApi from '../Api/Customapi';

export const SESSION_STATUS = {
  PENDING: 0,
  CANCELLED: 1,
  COMPLETED: 2,
  SKIPPED: 3,
};

export const SESSION_STATUS_LABELS = {
  0: 'Pending',
  1: 'Cancelled',
  2: 'Completed',
  3: 'Skipped',
};

export const SESSION_STATUS_CONFIG = {
  0: { label: 'Pending', color: 'warning', bgLight: 'bg-amber-50', borderColor: 'border-amber-200', textColor: 'text-yellow-600' },
  1: { label: 'Cancelled', color: 'error', bgLight: 'bg-red-50', borderColor: 'border-red-200', textColor: 'text-red-600' },
  2: { label: 'Completed', color: 'success', bgLight: 'bg-green-50', borderColor: 'border-green-200', textColor: 'text-green-600' },
  3: { label: 'Skipped', color: 'default', bgLight: 'bg-gray-50', borderColor: 'border-gray-200', textColor: 'text-gray-600' },
};


export const usePatientSessions = (params = {}) => {
  const {
    fromDate,
    toDate,
    status,
    pageNumber = 1,
    pageSize = 20,
  } = params;

  return useQuery({
    queryKey: ['patientSessions', { fromDate, toDate, status, pageNumber, pageSize }],
    queryFn: async () => {
      const queryParams = {
        PageNumber: pageNumber,
        PageSize: pageSize,
      };

      if (fromDate) queryParams.FromDate = fromDate;
      if (toDate) queryParams.ToDate = toDate;
      if (status !== undefined && status !== null) queryParams.Status = status;

      const response = await drEasyApi.get('/api/PatientSessions/GetMySessions', {
        params: queryParams,
      });

      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });
};

/**
 * Hook for searching patient sessions with filters
 * @param {Object} params - Search parameters
 * @param {string} params.specialistId - Specialist ID
 * @param {number} params.userId - User ID
 * @param {string} params.fromDate - Start date
 * @param {string} params.toDate - End date
 * @param {number} params.status - Session status
 * @param {number} params.pageNumber - Page number
 * @param {number} params.pageSize - Page size
 * @returns {Object} Query result
 */
export const useSearchPatientSessions = (params = {}) => {
  const {
    specialistId,
    userId,
    fromDate,
    toDate,
    status,
    pageNumber = 1,
    pageSize = 20,
  } = params;

  return useQuery({
    queryKey: ['searchPatientSessions', { specialistId, userId, fromDate, toDate, status, pageNumber, pageSize }],
    queryFn: async () => {
      const queryParams = {
        PageNumber: pageNumber,
        PageSize: pageSize,
      };

      if (specialistId) queryParams.SpecialistId = specialistId;
      if (userId) queryParams.UserId = userId;
      if (fromDate) queryParams.FromDate = fromDate;
      if (toDate) queryParams.ToDate = toDate;
      if (status !== undefined && status !== null) queryParams.Status = status;

      const response = await drEasyApi.get('/api/PatientSessions/SearchSessions', {
        params: queryParams,
      });

      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook for updating session status
 * @returns {Object} Mutation object
 */
export const useUpdateSessionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const { sessionId, newStatus, cancelReason, noteText, additionalNote } = data;

      const response = await drEasyApi.put('/api/PatientSessions/UpdateStatus', {
        sessionId,
        newStatus,
        cancelReason,
        noteText,
        additionalNote,
      });

      return response.data;
    },
    onSuccess: () => {
      // Invalidate all session queries
      queryClient.invalidateQueries({
        queryKey: ['patientSessions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['searchPatientSessions'],
      });
      message.success('Session status updated successfully');
    },
    onError: (error) => {
      const errorMessage = error.response?.data[0] || 'Failed to update session status';
      message.error(errorMessage);
    },
  });
};

/**
 * Hook for completing a session with notes
 * @returns {Object} Mutation object
 */
export const useCompleteSession = () => {
  return useUpdateSessionStatus();
};

/**
 * Hook for cancelling a session
 * @returns {Object} Mutation object
 */
export const useCancelSession = () => {
  return useUpdateSessionStatus();
};